import { useCallback } from 'react';

import {
  EmailRequestType,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  useCommunicationIntentsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { getLabelIdsForAction } from '../../../utils/emailLabelHelpers';

export const useMoveToInboxUserCommunication = () => {
  const {
    setMoveToInboxLoading,
    updateUserCommunicationLabels,
    getUserCommunicationByMessageId,
  } = useUserCommunicationsStore();
  const { setLabelsIntent, clearIntent, settleIntent } =
    useCommunicationIntentsStore();

  const handleMoveToInboxUserCommunication = useCallback(
    async (messageIds: string | string[], onSuccess?: () => void) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      const communicationsToMove = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        );

      if (communicationsToMove.length === 0) {
        log.error('No valid communications to move to inbox');
        return;
      }

      onSuccess?.();

      setMoveToInboxLoading(true);

      const labelBackupMap = new Map<string, LabelId[]>();

      communicationsToMove.forEach(({ messageId, communication }) => {
        labelBackupMap.set(messageId, communication.labelIds || []);
        const expectedLabelIds = getLabelIdsForAction(
          communication.labelIds,
          'inbox',
        );

        setLabelsIntent({
          messageId,
          threadMessageId: communication.threadMessageId,
          internetMessageId: communication.internetMessageId,
          originalLabelIds: communication.labelIds,
          expectedLabelIds,
        });
        updateUserCommunicationLabels(messageId, expectedLabelIds);
      });

      try {
        const operations = communicationsToMove.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              if (!communication.platform) {
                throw new Error('No platform found in communication');
              }

              await modifyEmail({
                userEmailAddress: communication.userEmailAddress,
                platform: communication.platform,
                messageId: communication.messageId,
                action: EmailRequestType.INBOX,
              });

              settleIntent(messageId);

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to move email to inbox for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            communicationsToMove.length === 1
              ? 'Message moved to inbox successfully'
              : `${communicationsToMove.length} messages moved to inbox successfully`,
          );
        } else {
          failed.forEach(({ messageId }) => {
            const previousLabels = labelBackupMap.get(messageId);
            if (previousLabels) {
              updateUserCommunicationLabels(messageId, previousLabels);
            }
            clearIntent(messageId);
          });

          log.warn(
            `${failed.length} operations failed out of ${communicationsToMove.length}`,
          );
          log.error(
            failed.length === communicationsToMove.length
              ? 'Failed to move message(s) to inbox'
              : `${failed.length} of ${communicationsToMove.length} messages failed to be moved to inbox`,
          );
        }
      } catch (error) {
        log.error('Failed to move message(s) to inbox:', error);
        messageIdsArray.forEach((messageId) => clearIntent(messageId));
      } finally {
        setMoveToInboxLoading(false);
      }
    },
    [
      setMoveToInboxLoading,
      updateUserCommunicationLabels,
      getUserCommunicationByMessageId,
      setLabelsIntent,
      clearIntent,
      settleIntent,
    ],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleMoveToInboxUserCommunication,
    extractMessageIdFromSelectedItems,
  };
};
